
import {
    JitterBufferMinDelay,
    Stage,
    StageEvents,
    SubscribeType,
} from 'amazon-ivs-web-broadcast';
import http from 'Infrastructure/HttpService/http.service';
import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { AppState } from 'services/redux-store';

import { JoinChannelOptions } from '../Entities/joined-channel-options';
import { JoinedChannel } from '../Entities/joined-channel.entity';
import { SimulcastMediaStream } from '../Entities/media-stream.entity';
import { StreamStatus } from '../Enums/stream-status.enum';

const useIvsMediaStream = (): {
    streamExistsForAlias: (alias: string) => boolean,
    assignStreamToVideoElementByAlias: (joinChannelOptions: JoinChannelOptions) => void,
    connectToStream: (newJoinedChannel: JoinedChannel, joinChannelOptions: JoinChannelOptions) => Promise<any>,
    forgetMediaStream: (alias: string) => void
} => {
    let [mediaStreams,] = useState<SimulcastMediaStream<MediaStream>[]>([]);
    const { bearerToken } = useSelector((state: AppState) => state.account);

    const getStreamByAlias = (alias: string): (SimulcastMediaStream<MediaStream> | undefined) => {
        return mediaStreams.find((s) => s.alias === alias);
    }

    const streamExistsForAlias = (alias: string): boolean => {
        const stream = getStreamByAlias(alias);

        if (stream?.stream) {
            return true;
        }

        return false;
    };

    const assignStreamToVideoElementByAlias = (joinChannelOptions: JoinChannelOptions): void => {
        const stream = getStreamByAlias(joinChannelOptions.alias);

        if (stream?.stream) {
            joinChannelOptions.videoElement.srcObject = stream.stream;
        }
    }

    const connectToStream = (newJoinedChannel: JoinedChannel, joinChannelOptions: JoinChannelOptions): Promise<any> => {
        return new Promise((resolve: any, reject: any) => {
            http.get(`/api/ivs/token/${joinChannelOptions.alias}`, {
                headers: {
                    Authorization: `Bearer ${bearerToken}`
                }
            }).then(response => {
                if (response.data) {

                    const strategy = {
                        subscribeConfiguration: () => {
                            return {
                                jitterBuffer: {
                                    minDelay: JitterBufferMinDelay.MEDIUM
                                }
                            };
                        },

                        stageStreamsToPublish() {
                            return [];
                        },

                        shouldPublishParticipant() {
                            return false;
                        },

                        shouldSubscribeToParticipant() {
                            return SubscribeType.AUDIO_VIDEO;
                        }
                    };

                    const stage = new Stage(response.data, strategy);

                    stage.on(
                        StageEvents.STAGE_PARTICIPANT_STREAMS_ADDED,
                        (participant, streams) => {
                            const mediaStream = new MediaStream();

                            streams.forEach((stream) => {

                                mediaStream.addTrack(stream.mediaStreamTrack);
                                joinChannelOptions.videoElement.srcObject = mediaStream;
                            });

                            addMediaStream(newJoinedChannel.alias, mediaStream, newJoinedChannel.videoElement?.id);
                            resolve(newJoinedChannel);
                        }
                    );

                    stage.join();
                }
            }).catch(reason =>{
                reject(reason);
            });
        });
    }

    const addMediaStream = (alias: string, stream: MediaStream, videoId?: string) => {
        if (mediaStreams.some((s) => s.alias === alias)) {
            return;
        }
        const tempMediaStreams = [...mediaStreams];

        mediaStreams = [];
        mediaStreams.push(...tempMediaStreams);
        mediaStreams.push({ alias, stream });
    };

    const forgetMediaStream = (alias: string) => {
        const index = mediaStreams.findIndex((x) => x.alias === (alias));
        if (index > -1)
            mediaStreams.splice(index, 1);
    };

    return { streamExistsForAlias, assignStreamToVideoElementByAlias, connectToStream, forgetMediaStream };
};

export default useIvsMediaStream;